import { Check, X, Send, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { PendingAction as ActionType } from '@/src/lib/types';
import { useState } from 'react';
import { sfx } from '@/src/lib/sfx';

interface PendingActionProps {
  action: ActionType & { priority?: string };
  onAction: (id: string, decision: 'accept' | 'reject' | 'forward' | 'cancel') => void;
}

const PRIORITY_RING: Record<string, string> = {
  urgent: 'ring-red-500/40 shadow-[0_0_24px_-12px_rgba(239,68,68,0.6)]',
  high: 'ring-orange-500/30',
  normal: 'ring-white/5',
  low: 'ring-white/5',
};
const PRIORITY_DOT: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-400',
  normal: 'bg-white/40',
  low: 'bg-neutral-500',
};

export default function PendingAction({ action, onAction }: PendingActionProps) {
  const [isProcessing, setIsProcessing] = useState<'accept' | 'reject' | 'forward' | 'cancel' | null>(null);

  const handleDecision = (decision: 'accept' | 'reject' | 'forward' | 'cancel') => {
    if (decision === 'accept') sfx.accept();
    else if (decision === 'reject') sfx.reject();
    else if (decision === 'forward') sfx.forward();
    setIsProcessing(decision);
    setTimeout(() => {
      onAction(action.id, decision);
    }, 600);
  };

  const isHandledExternally = !!action.handledBy;
  const priority = action.priority ?? 'normal';
  const priorityRing = PRIORITY_RING[priority] ?? PRIORITY_RING.normal;
  const priorityDot = PRIORITY_DOT[priority] ?? PRIORITY_DOT.normal;

  return (
    <motion.div
      layout
      initial={{ scale: 0.9, opacity: 0, y: 20 }}
      animate={{ 
        scale: isProcessing === 'forward' ? 0.4 : isProcessing ? 0.8 : 1, 
        opacity: isProcessing ? 0 : 1,
        y: isProcessing === 'accept' ? -60 : isProcessing === 'reject' ? 60 : isProcessing === 'forward' ? -150 : 0,
        rotateZ: isProcessing === 'forward' ? 25 : 0,
        x: isProcessing === 'forward' ? 400 : 0
      }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ 
        type: 'spring', 
        stiffness: isProcessing === 'forward' ? 100 : 500, 
        damping: isProcessing === 'forward' ? 15 : 30,
        opacity: { duration: 0.4 }
      }}
      className="group relative"
    >
      <div className={`bg-ink border rounded-2xl p-4 shadow-sm relative overflow-hidden transition-colors ring-1 ${priorityRing} ${isHandledExternally ? 'border-cream-line bg-warm-gray/50 opacity-80' : 'border-cream-line'}`}>
        {!isHandledExternally && (
          <span className={`absolute top-3 right-3 w-2 h-2 rounded-full ${priorityDot} ${priority === 'urgent' ? 'animate-pulse' : ''}`} />
        )}
        {/* Subtle Top Bar Detail */}
        <div className={`absolute top-0 inset-x-0 h-1 rounded-t-2xl opacity-10 transition-colors duration-300 ${
          isProcessing === 'accept' ? 'bg-green-500' : isProcessing === 'reject' ? 'bg-red-500' : isProcessing === 'forward' ? 'bg-blue-500' : 'bg-white'
        }`}></div>
        
        <div className="flex justify-between items-start">
          <div className="flex flex-col">
            <h4 className="text-sm font-semibold text-cream">{action.type}</h4>
            {isHandledExternally && (
              <span className="text-[10px] font-bold text-cream-muted/70 uppercase mt-0.5 flex items-center gap-1">
                {action.status === 'accepted' ? '✅' : '❌'} Handled by {action.handledBy}
              </span>
            )}
          </div>
          <AnimatePresence>
            {isProcessing && (
              <motion.div
                initial={{ scale: 0, opacity: 0, rotate: -45 }}
                animate={{ scale: 1.2, opacity: 1, rotate: 0 }}
                className={`p-1 rounded-full ${
                  isProcessing === 'accept' ? 'text-green-500' : 
                  isProcessing === 'reject' ? 'text-red-500' : 
                  isProcessing === 'forward' ? 'text-blue-500' : 'text-cream-muted/70'
                }`}
              >
                {isProcessing === 'accept' && <Check size={20} strokeWidth={3} />}
                {isProcessing === 'reject' && <X size={20} strokeWidth={3} />}
                {isProcessing === 'forward' && <Send size={20} strokeWidth={3} />}
                {isProcessing === 'cancel' && <RotateCcw size={20} strokeWidth={3} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <p className="text-xs text-cream mt-1 leading-relaxed">
          {action.reason}
        </p>
        
        <div className="flex flex-wrap gap-1.5 mt-3">
          {action.affectedSystems.map(system => (
            <span key={system} className="px-2 py-0.5 bg-warm-gray text-cream-muted rounded-md text-[10px] font-medium uppercase tracking-wider">
              {system}
            </span>
          ))}
          {action.status === 'forwarded' && (
            <span className="px-2 py-0.5 bg-blue-900/30 text-blue-400 text-[10px] font-bold uppercase tracking-wider">
              Forwarded
            </span>
          )}
        </div>
        
        {!isProcessing && !isHandledExternally && (
          <div className="grid grid-cols-2 gap-2 mt-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => handleDecision('reject')}
              className="h-10 flex items-center justify-center rounded-xl bg-warm-gray border border-cream-line text-xs font-semibold text-cream-muted hover:bg-warm-gray transition-all outline-none"
            >
              Reject
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => handleDecision('accept')}
              className="h-10 flex items-center justify-center rounded-xl bg-white text-black text-xs font-semibold hover:bg-neutral-100 transition-all outline-none"
            >
              Accept
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
